import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { 
  HiOutlineShieldCheck, 
  HiOutlineLockClosed
} from 'react-icons/hi2';

const sections = [
  { id: 'overview', title: '1. Overview & Commitment' },
  { id: 'data-collected', title: '2. Data We Collect' },
  { id: 'data-usage', title: '3. How We Use Data' },
  { id: 'payment-security', title: '4. Payment & Card Security' },
  { id: 'cookies', title: '5. Cookies & Analytics' },
  { id: 'data-sharing', title: '6. Third-Party Sharing' },
  { id: 'user-rights', title: '7. Your Privacy Rights' },
  { id: 'retention', title: '8. Data Retention' },
];

const PrivacyPolicy = () => {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="Learn how Feastic Services Pvt Ltd (Falhari) collects, protects, and respects your personal data."
      lastUpdated="October 09, 2026"
      sections={sections}
    >
      <div className="space-y-8 text-gray-700">
        
        {/* Intro Banner */}
        <div className="p-4 rounded-lg bg-orange-50/60 border border-orange-200/60 flex items-start gap-3">
          <HiOutlineShieldCheck className="text-xl text-[#FF410B] shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-normal">
            This Privacy Policy explains how <span className="font-semibold text-gray-900">Feastic Services Pvt Ltd</span> (“we”, “us”, “our”, “Falhari”) handles personal data collected through our website, mobile application, and fresh fruit subscription services.
          </p>
        </div>

        {/* Section 1 */}
        <section id="overview" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">1. Privacy Overview & Commitment</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            We are deeply committed to protecting your privacy in accordance with applicable Indian laws and regulations (including Information Technology Act guidelines). By using our website or ordering our fresh fruit products, you consent to the data practices described in this Policy.
          </p>
        </section>

        {/* Section 2 */}
        <section id="data-collected" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">2. Information We Collect</h2>
          <p className="text-sm leading-relaxed text-gray-700 mb-2">We may collect the following personal and operational data:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <span className="font-semibold text-gray-900 text-xs block mb-1">Contact Details</span>
              <span className="text-xs text-gray-600">Name, mobile phone number, email address, and delivery location address.</span>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <span className="font-semibold text-gray-900 text-xs block mb-1">Order Preferences</span>
              <span className="text-xs text-gray-600">Dietary preferences, order history, subscription choices, and feedback.</span>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <span className="font-semibold text-gray-900 text-xs block mb-1">Technical Data</span>
              <span className="text-xs text-gray-600">IP address, device type, browser settings, and page interaction logs.</span>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <span className="font-semibold text-gray-900 text-xs block mb-1">Customer Service Logs</span>
              <span className="text-xs text-gray-600">Communication notes from WhatsApp, phone calls, or email support queries.</span>
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section id="data-usage" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">3. How We Use Your Gathered Data</h2>
          <p className="text-sm leading-relaxed text-gray-700 mb-2">Your information is gathered to provide seamless fresh fruit delivery services, including:</p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>Fulfilling daily fresh food orders and managing delivery logistics.</li>
            <li>Sending order status notifications, dispatch alerts, and invoice details.</li>
            <li>Improving our fruit salad recipes, cold-pressed juice formulations, and website user experience.</li>
            <li>Communicating promotional offers, seasonal fruit arrivals, and loyalty rewards.</li>
            <li>Internal record keeping, audits, and quality control.</li>
          </ul>
        </section>

        {/* Section 4 - Card Security Highlight */}
        <section id="payment-security" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">4. Payment & Card Data Safeguards</h2>
          <div className="p-4 rounded-lg bg-emerald-50/70 border border-emerald-200/80 flex items-start gap-3">
            <HiOutlineLockClosed className="text-xl text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-emerald-950 font-normal leading-relaxed">
              <span className="font-semibold">Zero Card Data Storage Guarantee:</span> In compliance with Indian laws and RBI guidelines, Feastic Services Pvt Ltd does <span className="font-semibold underline">not</span> store any credit card, debit card, or sensitive banking credentials on our servers. All financial transactions are processed securely through PCI-DSS compliant payment gateways.
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section id="cookies" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">5. Cookies & Web Analytics</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            We use essential cookies and web analytics to understand user navigation, remember your cart items, and personalize your experience. You can choose to disable cookies through your browser settings, though certain interactive features on our site may become limited as a result.
          </p>
        </section>

        {/* Section 6 */}
        <section id="data-sharing" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">6. Third-Party Data Sharing</h2>
          <p className="text-sm leading-relaxed text-gray-700 mb-2">We do not sell or rent your personal data to third-party marketers. Data is shared strictly on a need-to-know basis with:</p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
            <li>Logistics and delivery partners responsible for bringing your order to your doorstep.</li>
            <li>Verified cloud hosting, analytics, and messaging infrastructure providers.</li>
            <li>Law enforcement or regulatory bodies when required under applicable legal statutes.</li>
          </ul>
        </section>

        {/* Section 7 */}
        <section id="user-rights" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">7. Your Privacy Rights & Consent Withdrawal</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            You have the right to inspect, update, or request correction of your stored profile details. You may also withdraw your consent for promotional communications at any time by sending an email to <a href="mailto:hello@falhari.com" className="text-[#FF410B] font-semibold underline">hello@falhari.com</a>.
          </p>
        </section>

        {/* Section 8 */}
        <section id="retention" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">8. Data Retention & Security</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            We retain personal data for as long as necessary to fulfill active subscriptions, maintain accounting compliance, and meet statutory requirements under Indian law. We implement industry-standard physical and electronic safeguards to protect data from unauthorized access.
          </p>
        </section>

      </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
